import { X, Mail, CalendarDays, Users, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface EntenteZicatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EntenteZicatModal({ isOpen, onClose }: EntenteZicatModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl bg-white shadow-2xl border border-amber-100 flex flex-col"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-amber-100 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-500 px-6 py-4 text-white rounded-t-2xl">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 shadow-inner">
                  <Mail className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold leading-snug">
                    Entente bonus post-vente – François Zicat
                  </h2>
                  <p className="mt-1 text-sm text-amber-100">
                    Traçabilité officielle de l&apos;entente de bonus basée sur les dividendes bi-annuels,
                    consignée par courriel pour le Conseil d&apos;Administration.
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-full bg-white/15 p-1.5 text-white hover:bg-white/25 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Bandeau résumé exécutif */}
              <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-amber-900">
                  <FileText className="h-4 w-4" />
                  <p className="text-sm font-semibold uppercase tracking-wide">
                    Résumé exécutif de l&apos;entente
                  </p>
                </div>
                <p className="text-sm text-amber-900 leading-relaxed">
                  Bonus post-vente accordé à <strong>François Zicat</strong> sur le dividende bi-annuel
                  versé par l&apos;entreprise, <strong>jusqu&apos;au 31 décembre 2027</strong>, selon les
                  paramètres suivants&nbsp;:
                  <br />
                  • <strong>2024–2025</strong> : bonus de <strong>3&nbsp;%</strong> du dividende bi-annuel.
                  <br />
                  • <strong>2026–2027</strong> : bonus de <strong>5&nbsp;%</strong> du dividende bi-annuel.
                  <br />
                  Pour le calcul du bonus de <strong>décembre 2025</strong>, le pourcentage de dividende
                  utilisé est le même que celui appliqué en <strong>juin 2025</strong>.
                  <br />
                  <strong>Modalité de versement :</strong> le versement doit être traité comme un <strong>dividende discrétionnaire</strong>, et non comme une commission ou des honoraires; le paiement est effectué à la <strong>fiducie</strong>, laquelle l&apos;attribue à <strong>Gestion FZ</strong>, selon une structure <strong>validée par DFK</strong>.
                  <br />
                  L&apos;entente est consignée par courriel afin de garantir la traçabilité, même en l&apos;absence de document
                  contractuel formel.
                </p>
              </div>

              {/* Métadonnées du courriel */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 space-y-2 text-sm text-gray-800">
                <div className="flex items-center gap-2 text-gray-700">
                  <CalendarDays className="h-4 w-4 text-gray-500" />
                  <span>
                    <span className="font-semibold">Date :</span>{' '}
                    Mardi 08 juillet 2025, 14&nbsp;h&nbsp;02
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="space-y-1">
                    <p>
                      <span className="font-semibold">De :</span>{' '}
                      Dany Bourassa &lt;dany.bourassa@gestionbabou.com&gt;
                    </p>
                    <p>
                      <span className="font-semibold">À :</span>{' '}
                      Mélissa Pelletier &lt;melissa.pelletier@bzinc.ca&gt;
                    </p>
                    <p className="flex items-start gap-1">
                      <span className="font-semibold">Cc :</span>
                      <span>
                        Dany Bourassa &lt;dany.bourassa@bzinc.ca&gt;;{' '}
                        François Zicat &lt;fzicat@bzinc.ca&gt;;{' '}
                        François Delisle &lt;fdelisle@bzinc.ca&gt;
                      </span>
                    </p>
                    <p>
                      <span className="font-semibold">Objet :</span>{' '}
                      Entente bonus post-vente – François Zicat
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-700 pt-1">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span>
                    <span className="font-semibold">Impact gouvernance :</span>{' '}
                    Suivi par le CA dans la section ententes et engagements post-transaction.
                  </span>
                </div>
              </div>

              {/* Verbatim du courriel */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Courriel original (verbatim)
                  </p>
                </div>
                <div className="px-5 py-4 text-sm leading-relaxed text-gray-800 whitespace-pre-line">
{`Bonjour Mélissa,

Je t’écris afin de garder une trace écrite de l’entente conclue avec François Zicat dans le cadre de la vente de ses actions.

Suite à la transaction, François Zicat aura droit à un bonus correspondant à 5 % du montant du dividende bi-annuel versé par l’entreprise, et ce, jusqu’au 31 décembre 2027.

À noter que pour le calcul du bonus de décembre 2025, nous utiliserons le même pourcentage de dividende que celui appliqué en juin 2025.

Il est possible qu’aucun document officiel ne soit rédigé pour formaliser cette entente. C’est précisément pour éviter toute perte d’information que je consigne le tout par courriel.

Merci de conserver cette information dans nos dossiers.

Bonne journée,

En CC @Francois Zicat et @Francois Delisle



Dany Bourassa`}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


